import { Injectable } from '@angular/core';
import { buildEnrichedUserAgent, resolveDeviceLabelFromNavigator } from '../utils/device-label.util';

@Injectable({
	providedIn: 'root',
})
export class UserAgentService {
	private enrichedUserAgent: string | null = null;
	private deviceLabel: string | null = null;
	private resolvePromise: Promise<void> | null = null;

	public isIOS(): boolean {
		return (
			/iPad|iPhone|iPod/.test(navigator.userAgent) ||
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
		);
	}

	public isAndroid(): boolean {
		return /Android/.test(navigator.userAgent);
	}

	public isMobile(): boolean {
		return this.isIOS() || this.isAndroid();
	}

	public isPC(): boolean {
		const ua = navigator.userAgent;
		const isWindows = ua.includes('Windows');
		const isMac = ua.includes('Macintosh') && !('ontouchend' in document);
		const isLinux = ua.includes('X11') || ua.includes('Linux');
		const isDesktop = isWindows || isMac || isLinux;

		const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

		return isDesktop && !isMobile;
	}

	/**
	 * Resuelve etiqueta de dispositivo (UA-CH) y el UA enriquecido en memoria (telemetría).
	 */
	public async resolveAndCacheEnrichedUserAgent(): Promise<void> {
		if (this.resolvePromise) {
			return this.resolvePromise;
		}
		this.resolvePromise = this.doResolveAndCache();
		return this.resolvePromise;
	}

	private async doResolveAndCache(): Promise<void> {
		const rawUserAgent = navigator.userAgent;
		try {
			this.deviceLabel = await resolveDeviceLabelFromNavigator(this.isMobile());
		} catch {
			this.deviceLabel = null;
		}
		this.enrichedUserAgent = buildEnrichedUserAgent(this.deviceLabel, rawUserAgent);
	}

	/** User-Agent nativo del navegador (cabecera HTTP sin modificar). */
	public getNativeUserAgent(): string {
		return navigator.userAgent;
	}

	/** Valor fusionado para logs locales: `{modelo} | {UA}` o solo UA nativo. */
	public getEnrichedUserAgent(): string {
		return this.enrichedUserAgent ?? navigator.userAgent;
	}

	/** Etiqueta de dispositivo sin prefijo (null si no hay Client Hints útil). */
	public getDeviceLabel(): string | null {
		return this.deviceLabel;
	}

	public hasDeviceLabelPrefix(): boolean {
		return Boolean(this.deviceLabel?.trim());
	}
}
